import clsx from 'clsx';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';

import { PrimaryButton } from '@components/Button/variants';
import { useCurrentUser, usePollVote } from '@hooks';
import { subscribe_path } from '@routes';
import { FORUM_POSTS_KEY, ForumPost } from '@utils/backend-api/forumPosts';
import { Poll, isClosedPoll } from '@utils/backend-api/polls';

export default function PollCard({
  poll,
  forumPostId,
  showQuestion = true,
}: {
  poll: Poll;
  forumPostId: number | string;
  showQuestion?: boolean;
}) {
  const { currentUser } = useCurrentUser();
  const { mutate: vote, isLoading } = usePollVote();
  const queryClient = useQueryClient();
  const [localPoll, setLocalPoll] = useState<Poll | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);

  const activePoll = localPoll || poll;

  const isClosed = isClosedPoll(activePoll);
  const canVote = !!currentUser && !isClosed;
  const showResults = isClosed || activePoll.user_voted;
  const isCurrentUserSubscriber = currentUser?.is_subscriber;

  // Selecting/clicking an option immediately casts (or changes) the vote, no submit button needed
  const handleVote = (optionId: number) => {
    if (isLoading || optionId === activePoll.user_voted_option_id) return;

    setSelectedOptionId(optionId);
    vote(
      { forumPostId, pollOptionId: optionId },
      {
        onSuccess: (updatedForumPost: ForumPost) => {
          if (updatedForumPost.poll) {
            setLocalPoll(updatedForumPost.poll);
          }
          queryClient.invalidateQueries(FORUM_POSTS_KEY);
        },
        onError: () => {
          toast.error('Unable to cast your vote, please try again.');
        },
      }
    );
  };

  return (
    <div className="rounded-4 p-16" data-test="poll-card">
      {showQuestion && (
        <div className="mb-12 font-suisse-neue text-[18px] font-medium md:text-[20px]">
          {activePoll.question}
        </div>
      )}
      {!isCurrentUserSubscriber ? (
        <div className="relative mb-16">
          <div
            className="flex cursor-not-allowed flex-col gap-12"
            style={{ filter: 'blur(4px)' }}
            aria-disabled
          >
            {activePoll.poll_options.map((option) => (
              <div
                key={option.text}
                className={clsx(
                  'relative flex cursor-not-allowed flex-col gap-4 rounded-4 border border-solid border-grayscale-beige px-16 py-12'
                )}
              >
                <div className="flex justify-between">
                  <div className="relative z-[1] font-suisse-screen text-body-md">
                    {option.text}
                  </div>
                  <div className="relative z-[1] font-suisse-screen text-body-md">
                    {option.percentage}%
                  </div>
                </div>
                <div
                  className="absolute left-0 top-0 h-full w-full bg-primary-pink"
                  style={{ width: `${option.percentage}%` }}
                ></div>
              </div>
            ))}
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <PrimaryButton as="link" href={subscribe_path()} size="small">
              Join now to view the results
            </PrimaryButton>
          </div>
        </div>
      ) : (
        <div className="mb-16 flex flex-col gap-12">
          {activePoll.poll_options.map((option) =>
            showResults ? (
              <div
                key={option.id}
                onClick={canVote ? () => handleVote(option.id) : undefined}
                className={clsx(
                  'relative flex flex-col gap-4 rounded-4 border border-solid border-grayscale-beige px-16 py-12',
                  { 'cursor-pointer hover:border-primary-magenta': canVote }
                )}
              >
                <div className="flex justify-between">
                  <div className="relative z-[1] font-suisse-screen text-body-md">
                    {option.text}
                  </div>
                  <div className="relative z-[1] font-suisse-screen text-body-md">
                    {option.percentage}%
                  </div>
                </div>
                <div
                  className="absolute left-0 top-0 h-full w-full bg-primary-pink"
                  style={{ width: `${option.percentage}%` }}
                ></div>
              </div>
            ) : (
              <label
                key={option.id}
                className={clsx(
                  'relative flex cursor-pointer items-center border border-solid border-grayscale-beige px-16 py-12 hover:border-primary-magenta',
                  {
                    'border-none': selectedOptionId === option.id,
                  }
                )}
              >
                <input
                  type="radio"
                  name={`poll-${activePoll.id}`}
                  className="mr-8 hidden flex-shrink-0"
                  checked={selectedOptionId === option.id}
                  onChange={() => handleVote(option.id)}
                />
                <span className="relative z-[1] font-suisse-screen text-body-md">
                  {option.text}
                </span>
                {selectedOptionId === option.id && (
                  <div className="absolute right-0 top-0 h-full w-full bg-primary-pink" />
                )}
              </label>
            )
          )}
        </div>
      )}

      {isClosed && (
        <div className="flex items-center justify-between gap-8 text-[14px] text-grayscale-gray">
          <span>Poll closed</span>
        </div>
      )}
    </div>
  );
}
